import { AccessCardData, Booking, BookingCreate, BookingListResponse, MeetingClientContext, MeetingGuestContext } from '../types/booking';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

function getToken(): string {
  return localStorage.getItem('token') ?? '';
}

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getToken()}`,
  };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(err.detail ?? 'Request failed');
  }
  return res.json();
}

export const bookingService = {
  async create(data: BookingCreate): Promise<Booking> {
    const res = await fetch(`${API_URL}/api/v1/bookings`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<Booking>(res);
  },

  async list(): Promise<BookingListResponse> {
    const res = await fetch(`${API_URL}/api/v1/bookings`, { headers: authHeaders() });
    return handleResponse<BookingListResponse>(res);
  },

  async get(id: string): Promise<Booking> {
    const res = await fetch(`${API_URL}/api/v1/bookings/${id}`, { headers: authHeaders() });
    return handleResponse<Booking>(res);
  },

  async cancel(id: string): Promise<void> {
    const res = await fetch(`${API_URL}/api/v1/bookings/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(err.detail ?? 'Request failed');
    }
  },

  async getAccessCard(bookingId: string): Promise<AccessCardData> {
    const res = await fetch(`${API_URL}/api/v1/bookings/${bookingId}/access-card`, {
      headers: authHeaders(),
    });
    return handleResponse<AccessCardData>(res);
  },

  async generateAccessCard(bookingId: string): Promise<AccessCardData> {
    const res = await fetch(`${API_URL}/api/v1/bookings/${bookingId}/access-card/generate`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return handleResponse<AccessCardData>(res);
  },

  async joinMeetingLink(token: string, isGuest: boolean): Promise<MeetingClientContext | MeetingGuestContext> {
    const url = isGuest
      ? `${API_URL}/api/v1/bookings/join/${token}?guest=1`
      : `${API_URL}/api/v1/bookings/join/${token}`;
    const headers = isGuest
      ? { 'Content-Type': 'application/json' }
      : authHeaders();
    const res = await fetch(url, { headers });
    return handleResponse<MeetingClientContext | MeetingGuestContext>(res);
  },

  async startMeetingSession(
    bookingId: string,
    deviceFingerprint: string,
  ): Promise<{ status: 'ok' } | { status: 'conflict'; device_fingerprint: string }> {
    const res = await fetch(`${API_URL}/api/v1/bookings/${bookingId}/meeting/start`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ device_fingerprint: deviceFingerprint }),
    });
    if (res.status === 409) {
      const body = await res.json().catch(() => ({ device_fingerprint: '' }));
      return { status: 'conflict', device_fingerprint: body.device_fingerprint ?? '' };
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(err.detail ?? 'Request failed');
    }
    return { status: 'ok' };
  },

  async transferMeetingSession(bookingId: string, deviceFingerprint: string): Promise<void> {
    const res = await fetch(`${API_URL}/api/v1/bookings/${bookingId}/meeting/transfer`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ device_fingerprint: deviceFingerprint }),
    });
    return handleResponse<void>(res);
  },

  async endMeetingSession(bookingId: string): Promise<void> {
    const res = await fetch(`${API_URL}/api/v1/bookings/${bookingId}/meeting/end`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return handleResponse<void>(res);
  },

  async acknowledgeFais(bookingId: string): Promise<{ acknowledged: boolean }> {
    const res = await fetch(
      `${API_URL}/api/v1/bookings/${bookingId}/meeting/acknowledge-fais`,
      { method: 'POST', headers: authHeaders() },
    );
    return handleResponse<{ acknowledged: boolean }>(res);
  },

  async sendMeetingMessage(bookingId: string, content: string): Promise<void> {
    const res = await fetch(`${API_URL}/api/v1/bookings/${bookingId}/meeting/message`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ content }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(err.detail ?? 'Request failed');
    }
  },

  async getMeetingMessages(
    bookingId: string,
  ): Promise<Array<{ id: string; role: string; content: string; created_at: string }>> {
    const res = await fetch(`${API_URL}/api/v1/bookings/${bookingId}/meeting/messages`, {
      headers: authHeaders(),
    });
    return handleResponse<
      Array<{ id: string; role: string; content: string; created_at: string }>
    >(res);
  },
};
